import { Select, SelectProps, Spin } from 'antd'
import { debounce } from 'lodash'
import React, { useEffect } from 'react'

export interface DebounceSelectProps<ValueType = any> extends Omit<SelectProps<ValueType>, 'options' | 'children'> {
  fetchOptions: (search: string) => Promise<ValueType[]>
  debounceTimeout?: number
  optionsFilter?: (options: OptionsType) => OptionsType
}

function DebounceSelect({
  fetchOptions,
  debounceTimeout = 500,
  optionsFilter,
  onChange,
  defaultValue = [],
  defaultOptions,
  ...props
}: DebounceSelectProps & { defaultOptions?: any }) {
  const [fetching, setFetching] = React.useState(false)
  const [options, setOptions] = React.useState<OptionsType>([])
  const fetchRef = React.useRef(0)

  useEffect(() => {
    if (defaultOptions) {
      setOptions(defaultOptions)
    }
  }, [defaultOptions])

  const debounceFetcher = React.useMemo(() => {
    const loadOptions = (value: string) => {
      fetchRef.current += 1
      const fetchId = fetchRef.current
      setOptions([])
      setFetching(true)

      fetchOptions(value).then((newOptions) => {
        if (fetchId !== fetchRef.current) {
          // for fetch callback order
          return
        }

        setOptions(newOptions)
        setFetching(false)
      })
    }

    return debounce(loadOptions, debounceTimeout)
  }, [fetchOptions, debounceTimeout])

  return (
    <Select
      // labelInValue
      filterOption={false}
      onSearch={debounceFetcher}
      notFoundContent={fetching ? <Spin size="small" /> : null}
      {...props}
      onChange={onChange}
      defaultValue={defaultValue}
      options={optionsFilter?.(options) ?? options}
    />
  )
}

export default DebounceSelect
